import React, { useState, useEffect, useCallback } from 'react';
import { PANE_WIDE_WIDTH } from '../constants/paneLayout';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { ScreenLayout } from '../components/ScreenLayout';
import { usePaneShellConfig } from '../components/PersistentPaneShell';
import { Header } from '../components/Header';
import { FormHeader } from '../components/FormHeader';
import { useGoBack } from '../hooks/useGoBack';
import { ContactsList, Contact, ContactsFilterParams } from '../components/ContactsList';
import { ConversationScreen } from './ConversationScreen';
import { CallHistoryScreen } from './CallHistoryScreen';
import { PaneHeader, PaneHeaderIconButton } from '../components/PaneHeader';
import { ConversationIdentifierDropdown } from '../components/ConversationIdentifierDropdown';
import { useContactConversationPane } from '../hooks/useContactConversationPane';
import { WhatsAppContactModal } from '../components/WhatsAppContactModal';
import { MoreOptionsModal } from '../components/MoreOptionsModal';
import { ContactEditForm, getChannelName } from '../components/ContactEditForm';
import { ScopedListBanner } from '../components/data-lists/ScopedListBanner';
import { ContactsExportDialog } from '../components/ContactsExportDialog';
import { generateEmptyCSVTemplate, downloadCSV, downloadBlob } from '../utils/csvExport';
import { ContactExportFieldsModal, ExportField } from '../components/ContactExportFieldsModal';
import { apiService } from '../services/api';
import { socketService } from '../services/socket.service';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { useIsMobileBrowser } from '../hooks/useIsMobileBrowser';
import { useDesktopPaneLayout } from '../hooks/useDesktopPaneLayout';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { usePermissions } from '../hooks/usePermissions';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface ContactsListScreenProps {
  /** Set when this screen is reached via a system Contacts Data List's
   * scoped mode (see DataListRecordsScreen.tsx) — pre-selects that list in
   * ContactsList's existing "filter by Data List" dropdown, and enables the
   * Extra Fields section (item 2) on the contact edit form for that list's
   * customColumns, if any. */
  scopedToDataListId?: string;
  scopedListName?: string;
  /** False when this screen is mounted inside PersistentPaneShell but
   * currently hidden (another screen in the group is active) — see
   * usePaneShellConfig's own doc comment for why this must gate shell
   * registration. Always true outside the shell (native/narrow mobile-web). */
  isActive?: boolean;
  /** A contact id read from the URL's `?contact=` query param at app boot —
   * restores that contact's profile+conversation on page load/refresh. See
   * useContactConversationPane's openContactById. */
  deepLinkContactId?: string | null;
  /** Called once the deep-linked contact is open, so later visits start on the screen itself. */
  onDeepLinkContactOpened?: () => void;
}

export const ContactsListScreen: React.FC<ContactsListScreenProps> = ({ scopedToDataListId, scopedListName, isActive = true, deepLinkContactId, onDeepLinkContactOpened }) => {
  const styles = useThemedStyles(getStyles);
  console.log('🎯 [ContactsListScreen] Component rendered');

  const { user, currentBusiness } = useAppStore();
  const contactsRefreshToken = useAppStore((state) => state.contactsRefreshToken);
  const { navigateTo, showNotification, activeTab, setActiveTab, startPhoneCall, isPhoneDialerReady, routeParams } = useAppContext();
  const goBack = useGoBack('home');
  const insets = useSafeAreaInsets();
  const { phoneAccount } = useProfileCompletion();
  const hasPhoneService = phoneAccount?.status === 'active';
  const isMobileBrowser = useIsMobileBrowser();
  const rtl = useRTLStyles();
  const { hasPermissionAtLeast } = usePermissions();
  const canManageContacts = hasPermissionAtLeast('contacts.manage', 'edit');
  const canExportContacts = hasPermissionAtLeast('contacts.export', 'edit');
  const canImportContacts = hasPermissionAtLeast('contacts.import', 'edit');
  // Client-side mirror of the export endpoint's own masking check (server
  // is still the actual authority — this only drives the "masked" hint text
  // in the field picker, not whether masking happens).
  const canViewMobileNumber = hasPermissionAtLeast('contacts.viewMobileNumber', 'view');
  const { t, locale } = useLocale();

  // Fixed export columns the field picker offers — same 7 ids the backend's
  // EXPORT_FIXED_FIELDS defaults to when no fields are picked at all, kept
  // in sync manually since one is a UI label list and the other a
  // value-extraction map.
  const EXPORT_FIXED_FIELDS: ExportField[] = [
    { id: 'name', label: t('contacts.sort.field.name') },
    { id: 'phone', label: t('contacts.sort.field.phone') },
    { id: 'email', label: t('contacts.sort.field.email') },
    { id: 'tags', label: t('contactsScreen.export.field.tags') },
    { id: 'city', label: t('contacts.sort.field.city') },
    { id: 'country', label: t('contacts.sort.field.country') },
    { id: 'nationality', label: t('contacts.sort.field.nationality') },
  ];

  // Desktop web gets the full master/detail layout (contacts list in the
  // persistent shell's left pane, selected/new contact in the middle) and is
  // routed through PersistentPaneShell by AppNavigator — this screen then
  // registers its chrome via usePaneShellConfig and returns bare content
  // instead of its own <ScreenLayout>. Native and narrow mobile-web instead
  // show the full list as this screen's own content, same as before, with
  // edit/add opening as a full-screen modal. Shared hook (not computed
  // locally) so this can never disagree with AppNavigator's own check for
  // which path it routed this screen through — see the hook's own doc
  // comment for the double-chrome bug that mismatch would cause.
  const isDesktopPaneLayout = useDesktopPaneLayout();
  
  // State
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showMoreModal, setShowMoreModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Opened from global search's "See all": start with that search.
  useEffect(() => {
    const q = (routeParams as any)?.q;
    if (typeof q === 'string' && q.trim()) setSearchQuery(q.trim());
  }, [(routeParams as any)?.q]);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [selectedContactProfileRefreshKey, setSelectedContactProfileRefreshKey] = useState(0);
  // Drives this screen's own middle (main content) area on desktop web —
  // NOT a ScreenLayout pane prop. 'empty' shows a "select a contact to
  // view" placeholder; 'edit-contact' shows ContactEditForm for whichever
  // of selectedContact (editing) or null (adding) is current.
  const [middlePaneContent, setMiddlePaneContent] = useState<'edit-contact' | 'empty'>('empty');
  // The contact's conversation, opened alongside its profile in the right
  // pane (desktop web only) — see useContactConversationPane's own doc
  // comment; this screen didn't use the right pane at all before this.
  const { viewedContact: conversationContact, conversationId: viewedConversationId, selectedChannel: conversationSelectedChannel, selectedIdentifierValue: conversationSelectedIdentifierValue, identifierOptions: conversationIdentifierOptions, switchConversationIdentifier, openContact: openContactConversation, openContactById, closeContact: closeContactConversation, callHistoryTarget, openCallHistory, closeCallHistory } = useContactConversationPane();

  const handleCallHistoryCall = async () => {
    if (!callHistoryTarget || !isPhoneDialerReady) return;
    let target = callHistoryTarget.phoneNumber;
    try {
      const res = await apiService.getContactDialTarget(callHistoryTarget.contactId, undefined, undefined, undefined, callHistoryTarget.phoneIndex);
      target = res.data?.phoneNumber || target;
    } catch (error) {
      console.error('[ContactsListScreen] Failed to resolve phone call target', error);
    }
    if (!target) return;
    startPhoneCall(target, callHistoryTarget.contactId, { name: callHistoryTarget.contactName || target });
  };

  // Keep this screen's own middle-pane state in sync with the hook —
  // covers both a direct row click (handleContactSelect already sets these
  // explicitly too) and a deep-link resolution (openContactById below),
  // which only updates the hook's own state.
  useEffect(() => {
    if (conversationContact) {
      setSelectedContact(conversationContact);
      setMiddlePaneContent('edit-contact');
    }
  }, [conversationContact]);

  // Restore a `?contact=<id>` deep link on page load/refresh — guarded on
  // !conversationContact so it never clobbers a contact the user already
  // opened (or changed) by clicking, and in practice only ever fires once.
  useEffect(() => {
    if (deepLinkContactId && !conversationContact) {
      openContactById(deepLinkContactId);
    }
    if (deepLinkContactId) onDeepLinkContactOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkContactId]);

  const [contactsRefreshTrigger, setContactsRefreshTrigger] = useState(0);
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showExportFieldsModal, setShowExportFieldsModal] = useState(false);
  const [exportCustomFieldKeys, setExportCustomFieldKeys] = useState<string[]>([]);
  const [pendingExportIds, setPendingExportIds] = useState<string[] | undefined>(undefined);
  // Mirrors ContactsList's own currently-active filter/sort params (via
  // onFiltersChanged below) — applied to "export all" so it exports the
  // same filtered set the user is looking at, not every contact in the
  // business regardless of what's searched/filtered on screen.
  const [activeContactsFilters, setActiveContactsFilters] = useState<ContactsFilterParams>({});
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);

  // Set active tab to contacts on mount
  useEffect(() => {
    console.log('🎯 [ContactsListScreen] Setting active tab to contacts');
    setActiveTab('contacts');
  }, [setActiveTab]);

  // Forcing the left pane open on entry now lives in PersistentPaneShell
  // itself (fires once per entry into the whole Home/Contacts/Chat group,
  // not once per screen — see its own mount effect) since the pane no
  // longer remounts when moving between those screens.

  // Refresh contacts list on assignment notifications
  useEffect(() => {
    const handleAppNotification = (data: any) => {
      try {
        const payload = typeof data?.data === 'string'
          ? JSON.parse(data.data)
          : (data?.data || data);

        const notificationType = payload?.type || data?.type;
        const action = payload?.action || data?.action;
        if (
          payload?.reloadContacts ||
          payload?.reloadAssignments ||
          action === 'removed' ||
          notificationType === 'new_assignment' ||
          notificationType === 'assignment_update'
        ) {
          console.log('🔄 [ContactsListScreen] Triggering contacts refresh from notification');
          setContactsRefreshTrigger(prev => prev + 1);
        }
      } catch (error) {
        console.warn('⚠️ [ContactsListScreen] Failed to parse app notification:', error);
      }
    };

    socketService.on('app-notification', handleAppNotification);
    return () => socketService.off('app-notification', handleAppNotification);
  }, []);

  // Handle tab press
  const handleTabNavigation = createTabPressHandler(navigateTo, setActiveTab, { onMore: () => setShowMoreModal(true) });
  const handleTabPress = (screenId: string) => {
    handleCloseMiddlePane();
    handleTabNavigation(screenId);
  };

  const selectedIds = React.useMemo(() => new Set(selectedContacts.map(c => c.id)), [selectedContacts]);
  const contactsWithWhatsApp = React.useMemo(
    () => selectedContacts.filter(
      c => (c.channel === 'whatsapp' || (c.linkedChannels || []).includes('whatsapp')) && (c.phoneNumber || c.phone)
    ),
    [selectedContacts]
  );
  const showBulkWhatsAppFAB = selectionMode && contactsWithWhatsApp.length > 0;

  const handleToggleSelect = (contact: Contact) => {
    setSelectedContacts(prev => {
      const exists = prev.some(c => c.id === contact.id);
      if (exists) return prev.filter(c => c.id !== contact.id);
      return [...prev, contact];
    });
  };

  const handleSelectAllFiltered = (contacts: Contact[]) => {
    setSelectedContacts(contacts);
  };

  const handleExitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedContacts([]);
  };

  // Exiting (or entering) a scoped Data List view navigates via the
  // "Exit to Contacts"/"Exit to Data Lists" banner (ScopedListBanner),
  // which just calls navigateTo — it doesn't unmount/remount this screen,
  // so selectionMode/selectedContacts (plain useState here) previously
  // survived across the scope change, leaving stale checkboxes checked
  // against a completely different contact set.
  useEffect(() => {
    handleExitSelectionMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedToDataListId]);

  const handleBulkWhatsAppPress = async () => {
    if (contactsWithWhatsApp.length === 0) return;
    // Never build the send list from c.phoneNumber/c.phone directly — those come from
    // the masked list endpoint (contacts.viewMobileNumber) and, for a masked user, would
    // hand a string like "+********6631" straight to the WhatsApp send API. Resolve REAL
    // numbers first, same pattern as handleCallPress's getContactDialTarget fix below.
    let resolved: Array<{ id: string; name: string; phoneNumber: string | null }>;
    try {
      const res = await apiService.getContactSendTargets(contactsWithWhatsApp.map(c => c.id));
      if (res.statusCode !== 1000 || !res.data) {
        showNotification('error', t('contactsScreen.errors.title'), t('contactsScreen.errors.resolveSendTargetsFailed'));
        return;
      }
      resolved = res.data.results;
    } catch (error) {
      console.error('[ContactsListScreen] Failed to resolve send targets:', error);
      showNotification('error', t('contactsScreen.errors.title'), t('contactsScreen.errors.resolveSendTargetsFailed'));
      return;
    }
    const bulkContacts = resolved
      .filter((r): r is { id: string; name: string; phoneNumber: string } => !!r.phoneNumber?.trim())
      .map(r => ({
        contactId: r.id,
        name: r.name || '',
        phoneNumber: r.phoneNumber.replace(/^\+/, ''),
      }));
    if (bulkContacts.length === 0) return;
    navigateTo('send-whatsapp-message', undefined, { bulkContacts });
    handleExitSelectionMode();
  };

  // Handle contact added
  const handleContactAdded = (contact: { name: string; phoneNumber: string }) => {
    console.log('✅ [ContactsListScreen] Contact added:', contact);
    setShowAddContactModal(false);
    showNotification('success', t('contactsScreen.notifications.contactAddedTitle'), t('contactsScreen.notifications.contactAddedMessage', { name: contact.name }));
    // Trigger refresh of contacts list
    setContactsRefreshTrigger(prev => prev + 1);
  };

  // Handle conversation started
  const handleConversationStarted = (contact: { name: string; phoneNumber: string; topic: string }) => {
    console.log('✅ [ContactsListScreen] Conversation started:', contact);
    setShowAddContactModal(false);
    showNotification('success', t('contactsScreen.notifications.conversationStartedTitle'), t('contactsScreen.notifications.conversationStartedMessage', { name: contact.name }));
    navigateTo('chat');
  };

  // Handle contact selection (for editing in the middle pane on desktop web,
  // modal on native or narrow mobile-web viewports)
  const handleContactSelect = (contact: any) => {
    console.log('📝 [ContactsListScreen] Contact selected for editing:', contact);

    if (isDesktopPaneLayout) {
      // Desktop web only — show profile in the middle pane, conversation in
      // the right pane, at the same time.
      setSelectedContact(contact);
      setMiddlePaneContent('edit-contact');
      openContactConversation(contact);
    } else {
      // Native or mobile-web — show modal with contact edit form
      setSelectedContact(contact);
      setShowEditContactModal(true);
    }
  };

  // Handle contact save
  const handleContactSave = (contact: any) => {
    console.log('💾 [ContactsListScreen] Contact saved:', contact);
    showNotification('success', t('contactsScreen.notifications.contactUpdatedTitle'), t('contactsScreen.notifications.contactUpdatedMessage', { name: contact.name }));
    // Close the middle pane's form (desktop web) or close modal
    // (native/mobile-web) back to the "select a contact" empty state.
    if (isDesktopPaneLayout) {
      setMiddlePaneContent('empty');
      closeContactConversation();
    } else {
      setShowEditContactModal(false);
    }
    setSelectedContact(null);
    // Trigger refresh of contacts list
    setContactsRefreshTrigger(prev => prev + 1);
  };

  // Handle contact delete
  const handleContactDelete = (contactId: string) => {
    console.log('🗑️ [ContactsListScreen] Contact deleted:', contactId);
    showNotification('success', t('contactsScreen.notifications.contactDeletedTitle'), t('contactsScreen.notifications.contactDeletedMessage'));
    // Close the middle pane's form (desktop web) or close modal
    // (native/mobile-web) — see handleContactSave's comment above.
    if (isDesktopPaneLayout) {
      setMiddlePaneContent('empty');
      closeContactConversation();
    } else {
      setShowEditContactModal(false);
    }
    setSelectedContact(null);
    // Trigger refresh of contacts list
    setContactsRefreshTrigger(prev => prev + 1);
  };

  // Handle close middle pane's contact form, back to the "select a contact" empty state
  const handleCloseMiddlePane = () => {
    console.log('❌ [ContactsListScreen] Closing middle pane editor');
    setMiddlePaneContent('empty');
    setSelectedContact(null);
    closeContactConversation();
  };

  const handleContactsUpdated = useCallback((updatedContacts: Contact[]) => {
    if (!selectedContact?.id) return;
    const updated = updatedContacts.find(c => c.id === selectedContact.id);
    if (updated && updated.updatedAt !== selectedContact.updatedAt) {
      setSelectedContact(updated);
      setSelectedContactProfileRefreshKey(prev => prev + 1);
    }
  }, [selectedContact?.id, selectedContact?.updatedAt]);

  // "Download CSV" header action opens a choice dialog (blank template vs
  // actual contacts) instead of exporting straight through — see
  // ContactsExportDialog. The template stays a client-side-generated blank
  // CSV (unchanged from before); actual contacts now come from the
  // server-side, masking-aware export endpoint (contacts.viewMobileNumber
  // applied there, same as Reports' export) instead of the old
  // unmasked client-side contactsToCSV path.
  const handleDownloadTemplate = () => {
    const csvContent = generateEmptyCSVTemplate();
    downloadCSV(csvContent, 'sendifier_contacts_template.csv');
    showNotification('success', t('contactsScreen.notifications.templateDownloadedTitle'), t('contactsScreen.notifications.templateDownloadedEmptyMessage'));
    setShowExportDialog(false);
  };

  // "Current selection"/"All contacts" no longer exports immediately — it
  // opens the field picker (ContactExportFieldsModal) so the user chooses
  // which columns and file format first, then handleConfirmExportFields
  // does the actual download. ids are captured here (selection can change
  // once the picker's own modal is open) and reused when the picker
  // confirms.
  const handleDownloadSelection = async () => {
    const ids = selectionMode && selectedContacts.length > 0 ? selectedContacts.map((c) => c.id) : undefined;
    setPendingExportIds(ids);
    setShowExportDialog(false);
    try {
      const res = await apiService.getContactCustomFieldKeys();
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setExportCustomFieldKeys(res.data);
      }
    } catch {
      // Field picker still works with just the fixed fields if this fails
    }
    setShowExportFieldsModal(true);
  };

  const handleConfirmExportFields = async (selectedFieldIds: string[], format: 'csv' | 'xlsx') => {
    try {
      setDownloadingCSV(true);
      const ids = pendingExportIds;
      const response = await apiService.exportContacts({
        ids,
        format,
        lang: locale === 'ar' ? 'ar' : 'en',
        fields: selectedFieldIds,
        // An explicit id set already IS the filter (selection-mode export)
        // — only apply the list's active filters when exporting "all",
        // so that export matches whatever's currently searched/filtered on
        // screen instead of literally every contact in the business.
        ...(ids ? {} : activeContactsFilters),
      });

      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }

      const dateStamp = new Date().toISOString().split('T')[0];

      if (Platform.OS === 'web') {
        if (format === 'csv') {
          const csvContent = await response.text();
          downloadCSV(csvContent, `sendifier_contacts_${dateStamp}.csv`);
        } else {
          const blob = await response.blob();
          downloadBlob(blob, `sendifier_contacts_${dateStamp}.xlsx`);
        }
        showNotification(
          'success',
          t('contactsScreen.notifications.exportCompleteTitle'),
          ids?.length
            ? t('contactsScreen.notifications.exportCompleteMessage', { count: ids.length })
            : t('contactsScreen.export.allExportedMessage')
        );
      } else {
        // Native file download/sharing isn't wired up yet — same gap the
        // old client-side export already had (downloadCSV's native branch
        // was a no-op TODO).
        showNotification('info', t('contactsScreen.notifications.exportCompleteTitle'), t('contactsScreen.export.nativeUnsupported'));
      }
      setShowExportFieldsModal(false);
    } catch (error: any) {
      console.error('❌ [ContactsListScreen] Error exporting contacts:', error);
      showNotification('error', t('contactsScreen.errors.title'), error.message || 'Failed to export contacts');
    } finally {
      setDownloadingCSV(false);
    }
  };

  const header = (
    <Header
      showNotifications={true}
      showMessages={true}
      onNotificationPress={() => navigateTo('notifications')}
      onMessagePress={() => navigateTo('chat')}
      showNavigation={Platform.OS === 'web'}
      activeTab={activeTab}
      onTabPress={handleTabPress}
    />
  );

  const formHeader = (
    <FormHeader
      title={scopedToDataListId ? (scopedListName || t('contactsScreen.title')) : t('contactsScreen.title')}
      onBack={goBack}
      actions={[
        ...(canExportContacts
          ? [{ icon: 'download-outline' as const, onPress: () => setShowExportDialog(true), loading: downloadingCSV, label: t('contactsScreen.actions.downloadCsv'), accessibilityLabel: t('contactsScreen.actions.downloadCsv') }]
          : []),
        ...(canImportContacts
          ? [{ icon: 'cloud-upload-outline' as const, onPress: () => navigateTo('bulk-contact-upload'), label: t('contactsScreen.actions.importContacts'), accessibilityLabel: t('contactsScreen.actions.importContacts') }]
          : []),
        ...(canManageContacts
          ? [{
              icon: 'person-add-outline' as const,
              primary: true,
              onPress: () => {
                if (isDesktopPaneLayout) {
                  setSelectedContact(null);
                  setMiddlePaneContent('edit-contact');
                } else {
                  setShowAddContactModal(true);
                }
              },
              accessibilityLabel: t('contactsScreen.actions.addContact'),
            }]
          : []),
      ]}
    />
  );

  // The clicked contact's conversation, embedded in the right pane alongside
  // its profile in the middle pane (see handleContactSelect) — new for this
  // screen, which didn't use the right pane at all before.
  const conversationRightPaneContent = conversationContact ? (
    <View style={{ flex: 1, height: '100%' }}>
      <PaneHeader
        titleContent={
          <ConversationIdentifierDropdown
            options={conversationIdentifierOptions}
            selectedChannel={conversationSelectedChannel}
            selectedValue={conversationSelectedIdentifierValue}
            formatChannelName={(channel) => getChannelName(channel, t)}
            onSelect={switchConversationIdentifier}
          />
        }
        trailing={<PaneHeaderIconButton icon="close" onPress={closeContactConversation} />}
      />
      {viewedConversationId ? (
        <ConversationScreen
          embedded
          conversationId={viewedConversationId}
          onNavigateBack={closeContactConversation}
          onViewContact={() => {}}
        />
      ) : (
        <View style={styles.conversationPaneLoading}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      )}
    </View>
  ) : undefined;

  // A tapped phone identifier's Call History, same pane slot as the
  // conversation above (useContactConversationPane keeps the two mutually
  // exclusive) — embedded so it stays inside the persistent shell instead of
  // navigating away to a full screen.
  const callHistoryRightPaneContent = callHistoryTarget ? (
    <View style={{ flex: 1, height: '100%' }}>
      <PaneHeader
        titleContent={
          <ConversationIdentifierDropdown
            options={conversationIdentifierOptions}
            selectedChannel={conversationSelectedChannel}
            selectedValue={conversationSelectedIdentifierValue}
            formatChannelName={(channel) => getChannelName(channel, t)}
            onSelect={switchConversationIdentifier}
          />
        }
        trailing={
          <View style={[styles.callHistoryPaneActions, { flexDirection: rtl.rowDirection }]}>
            {!!callHistoryTarget.phoneNumber && (
              <TouchableOpacity
                style={styles.callHistoryPaneCallButton}
                onPress={handleCallHistoryCall}
                activeOpacity={0.85}
              >
                <Ionicons name="call" size={18} color="#FFFFFF" />
              </TouchableOpacity>
            )}
            <PaneHeaderIconButton icon="close" onPress={closeContactConversation} />
          </View>
        }
      />
      <CallHistoryScreen
        embedded
        hideEmbeddedHeader
        contactId={callHistoryTarget.contactId}
        contactName={callHistoryTarget.contactName}
        phoneNumber={callHistoryTarget.phoneNumber}
        phoneIndex={callHistoryTarget.phoneIndex}
        onClose={closeCallHistory}
      />
    </View>
  ) : undefined;

  // Registered with the nearest PersistentPaneShell ancestor on desktop web
  // (AppNavigator routes 'contacts' through it there); a harmless no-op on
  // native/narrow mobile-web, where this screen renders its own
  // <ScreenLayout> directly below instead — see usePaneShellConfig's own
  // doc comment.
  usePaneShellConfig({
    header,
    formHeader,
    showBottomTabs: Platform.OS !== 'web',
    onTabPress: handleTabPress,
    onContactPress: handleContactSelect,
    contactsRefreshTrigger: contactsRefreshTrigger + contactsRefreshToken,
    activeContactId: conversationContact?.id,
    rightPaneContent: callHistoryTarget || conversationContact ? 'custom' : 'empty',
    rightPaneCustomContent: callHistoryRightPaneContent || conversationRightPaneContent,
    rightPaneTab: callHistoryRightPaneContent ? 'calls' : 'chat',
    // Wider than the default pane width — a conversation needs more room
    // than e.g. a settings/contacts picker, same 480px convention
    // PostsListScreen.tsx already uses for its own "needs more room" case.
    rightPaneWidthOverride: callHistoryTarget || conversationContact ? PANE_WIDE_WIDTH : undefined,
    onRightPaneClose: callHistoryTarget ? closeCallHistory : closeContactConversation,
  }, isActive);

  const content = (
      <View style={styles.screenRoot}>
      <View style={styles.mainContent}>
        {scopedToDataListId && (
          /* Scoped mode — this list is showing only records added through a
             system Contacts Data List. Matches the same indicator
             ProductsListScreen.tsx shows for its own scoped mode. Its own
             small row above the list — unlike the search bar below, this
             only ever appears situationally, so it isn't worth folding into
             the toolbar row. */
          <View style={[styles.section, isDesktopPaneLayout && styles.sectionDesktopSpacing, styles.headerSection]}>
            <View style={styles.headerContainer}>
              <ScopedListBanner
                moduleLabel="Contacts"
                moduleIcon="person-outline"
                moduleScreen="contacts"
              />
            </View>
          </View>
        )}

        {/* Native / narrow mobile-web — this screen's own main content IS
            the full contacts list (no pane layout available on these
            viewports); tapping a contact opens the full-screen edit modal
            below instead of a middle-pane form. */}
        {!isDesktopPaneLayout && (
          <View style={[styles.section, styles.contactsSection]}>
            <ContactsList
              showHeader={true}
              showTitleBar={false}
              showSearch={false}
              headerLeftContent={
                /* Rendered inside ContactsList's own header block (right above
                   its filter-chip row) instead of this screen's own separate
                   section above everything — that previously cost the list a
                   whole extra section purely for a search field. */
                <View style={[styles.searchContainer, { flexDirection: rtl.rowDirection }]}>
                  <Ionicons name="search" size={18} color={colors.text.secondary} />
                  <TextInput
                    style={[styles.searchInput, rtl.marginStart(spacing.sm), { textAlign: rtl.textStart }]}
                    placeholder={t('contactsScreen.searchPlaceholder')}
                    placeholderTextColor={colors.text.disabled}
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                  />
                  {searchQuery.length > 0 && (
                    <TouchableOpacity onPress={() => setSearchQuery('')}>
                      <Ionicons name="close-circle" size={18} color={colors.text.secondary} />
                    </TouchableOpacity>
                  )}
                </View>
              }
              showFilters={true}
              searchQuery={searchQuery}
              initialDataListFilter={scopedToDataListId ? { id: scopedToDataListId, name: scopedListName || '' } : undefined}
              onContactPress={handleContactSelect}
              onContactsUpdated={handleContactsUpdated}
              hasPhoneService={hasPhoneService}
              refreshTrigger={contactsRefreshTrigger + contactsRefreshToken}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onSelectAllFiltered={handleSelectAllFiltered}
              onExitSelectionMode={handleExitSelectionMode}
              onEnterSelectionMode={() => setSelectionMode(true)}
              onFiltersChanged={setActiveContactsFilters}
              scrollContentPaddingBottom={
                Platform.OS !== 'web' ? (50 + spacing.sm + insets.bottom) + spacing.lg : 0
              }
            />
          </View>
        )}

        {/* Desktop web — the list now lives in the forced-open left pane
            (leftPaneContent="contacts" above); this middle area shows
            whichever contact is selected/being added, or a placeholder when
            none is. */}
        {isDesktopPaneLayout && (
          <View style={[styles.section, styles.sectionDesktopSpacing, styles.middlePaneContainer]}>
            {middlePaneContent === 'edit-contact' ? (
              <ContactEditForm
                contact={selectedContact}
                onClose={handleCloseMiddlePane}
                onSave={handleContactSave}
                onDelete={handleContactDelete}
                compact={true}
                listId={scopedToDataListId}
                profileRefreshKey={selectedContactProfileRefreshKey}
                onProfileDataChange={() => setContactsRefreshTrigger(prev => prev + 1)}
                onChannelSelect={(channel, identifierValue, channelContactId) => selectedContact && openContactConversation(selectedContact, channel, identifierValue, channelContactId)}
                onViewCallHistory={(contactId, contactName, phoneNumber, phoneIndex) => openCallHistory(contactId, contactName, phoneNumber, phoneIndex)}
              />
            ) : (
              <View style={styles.emptySelectContact}>
                <Ionicons name="person-circle-outline" size={64} color={colors.text.secondary} />
                <Text style={styles.emptySelectContactTitle}>{t('contactsScreen.selectContact.title')}</Text>
                <Text style={styles.emptySelectContactHint}>{t('contactsScreen.selectContact.hint')}</Text>
              </View>
            )}
          </View>
        )}
      </View>

      </View>
  );

  // Rendered outside ScreenLayout/the shell entirely — the full-screen edit
  // fallback uses raw position:fixed/absolute (not RN's Modal, which portals
  // regardless of nesting), so it must stay a true sibling rather than get
  // nested inside whatever stacking/transform context `content` ends up in.
  const overlays = (
    <>
      {/* Add Contact Modal */}
      <WhatsAppContactModal
        visible={showAddContactModal}
        onClose={() => setShowAddContactModal(false)}
        onContactAdded={handleContactAdded}
        onConversationStarted={handleConversationStarted}
      />

      {/* More Options Modal */}
      <MoreOptionsModal
        visible={showMoreModal}
        onClose={() => setShowMoreModal(false)}
      />

      {/* Download CSV header action opens this choice first — blank
          template (client-side, unchanged) vs actual contacts (server-side,
          masked per contacts.viewMobileNumber, current selection when one
          is active else everything). */}
      <ContactsExportDialog
        visible={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        selectedCount={selectionMode ? selectedContacts.length : 0}
        onDownloadTemplate={handleDownloadTemplate}
        onDownloadSelection={handleDownloadSelection}
        exporting={downloadingCSV}
      />

      {/* Field picker shown after choosing "current selection"/"all
          contacts" above — lets the user choose exactly which columns
          (fixed + custom) and file format before the actual download
          request fires. */}
      <ContactExportFieldsModal
        visible={showExportFieldsModal}
        onClose={() => setShowExportFieldsModal(false)}
        fixedFields={EXPORT_FIXED_FIELDS.map((f) => ({
          ...f,
          maskedForCurrentUser: f.id === 'phone' && !canViewMobileNumber,
        }))}
        customFieldKeys={exportCustomFieldKeys}
        onConfirm={handleConfirmExportFields}
        exporting={downloadingCSV}
      />

      {/* Edit Contact — native and mobile-web (anywhere the desktop pane
          layout isn't used, per isDesktopPaneLayout). Rendered as a
          full-screen overlay, not the dialog-style Modal, so it reads as its
          own screen (matching the PhoneCallScreen full-screen overlay
          pattern) rather than a floating popup on a small viewport. */}
      {!isDesktopPaneLayout && showEditContactModal && selectedContact && (
        <View style={[styles.editContactFullScreen, { paddingTop: insets.top, paddingBottom: insets.bottom }]}>
          {/* Same 360 profile as the desktop middle pane (compact layout —
              header actions beside the avatar, channel icons, profile
              cards). Channel taps open the same conversation / call-history
              view as desktop's right pane, stacked full-screen above. */}
          <ContactEditForm
            contact={selectedContact}
            onClose={() => {
              closeContactConversation();
              setShowEditContactModal(false);
              setSelectedContact(null);
            }}
            onSave={handleContactSave}
            onDelete={handleContactDelete}
            compact={true}
            listId={scopedToDataListId}
            profileRefreshKey={selectedContactProfileRefreshKey}
            onProfileDataChange={() => setContactsRefreshTrigger(prev => prev + 1)}
            onChannelSelect={(channel, identifierValue, channelContactId) => openContactConversation(selectedContact, channel, identifierValue, channelContactId)}
            onViewCallHistory={(contactId, contactName, phoneNumber, phoneIndex) => openCallHistory(contactId, contactName, phoneNumber, phoneIndex)}
          />
        </View>
      )}

      {/* Phone layout: the desktop right pane's conversation / call-history
          view (identifier dropdown header + embedded conversation), stacked
          full-screen over the profile. Its close button clears the hook's
          conversation state, returning to the profile underneath. */}
      {!isDesktopPaneLayout && (callHistoryTarget || conversationContact) && showEditContactModal && (
        <View style={[styles.editContactFullScreen, { paddingTop: insets.top, paddingBottom: insets.bottom }]}>
          {callHistoryTarget ? callHistoryRightPaneContent : conversationRightPaneContent}
        </View>
      )}

      {/* Bulk WhatsApp FAB - rendered outside ScreenLayout so position:fixed works on web */}
      {showBulkWhatsAppFAB && (
        <TouchableOpacity
          style={[
            styles.bulkWhatsAppFAB,
            Platform.OS !== 'web' && { bottom: (50 + spacing.sm + insets.bottom) + spacing.md },
          ]}
          onPress={handleBulkWhatsAppPress}
          activeOpacity={0.9}
        >
          <Ionicons name="logo-whatsapp" size={28} color="white" />
          <Text style={styles.bulkWhatsAppFABText}>
            {t('contactsScreen.bulkSendWhatsApp', { count: contactsWithWhatsApp.length })}
          </Text>
        </TouchableOpacity>
      )}
    </>
  );

  // Desktop web: AppNavigator routes this screen through PersistentPaneShell,
  // which owns the actual <ScreenLayout> — this screen just returns its
  // content/overlays for the shell to place. Native/narrow mobile-web: no
  // shell exists, so this screen renders its own <ScreenLayout> exactly as
  // every screen outside the shell group still does.
  if (isDesktopPaneLayout) {
    return (
      <>
        {content}
        {overlays}
      </>
    );
  }

  return (
    <>
      <ScreenLayout
        header={header}
        showBottomTabs={Platform.OS !== 'web'}
        onTabPress={handleTabPress}
        onLeftPaneClose={() => {}}
        leftPaneContent="contacts"
        onContactPress={handleContactSelect}
        contactsRefreshTrigger={contactsRefreshTrigger + contactsRefreshToken}
        formHeader={formHeader}
      >
        {content}
      </ScreenLayout>
      {overlays}
    </>
  );
};

const getStyles = () => StyleSheet.create({
  screenRoot: {
    flex: 1,
    position: 'relative',
  },
  editContactFullScreen: {
    ...(Platform.OS === 'web'
      ? { position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0 }
      : { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }),
    zIndex: 100000,
    backgroundColor: colors.background.surface,
  },
  mainContent: {
    flex: 1,
  },
  conversationPaneHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  conversationPaneLoading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  callHistoryPaneActions: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  callHistoryPaneCallButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Confirmed real bug: this used to always add marginHorizontal on top of
  // ScreenLayout's OWN edge padding (spacing.screenPadding, applied to its
  // whole content area whenever disableScreenPadding isn't passed — it
  // isn't, here) — the two compounded to 32px lost on EACH side on native
  // (16 + 16), a big chunk of a phone's narrow width, making every list row
  // feel cramped. Kept as a plain content spacer here (no margin at all);
  // the extra "floating card" inset is added back ONLY for desktop web via
  // sectionDesktopSpacing below, where the much wider viewport can spare it
  // and it was presumably an intentional look there.
  section: {
    marginTop: spacing.sm,
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    marginBottom: spacing.sm,
  },
  sectionDesktopSpacing: {
    marginHorizontal: spacing.md,
  },
  headerSection: {
    flexShrink: 0,
  },
  contactsSection: {
    flex: 1,
    marginBottom: 0,
  },
  // Desktop web only — this screen's own main content area, now the
  // "middle pane" of the master/detail layout (list moved to the left
  // pane). Holds either the selected/new contact's ContactEditForm or the
  // emptySelectContact placeholder below.
  middlePaneContainer: {
    flex: 1,
    marginBottom: 0,
  },
  emptySelectContact: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
  },
  emptySelectContactTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
    marginTop: spacing.md,
    textAlign: 'center',
  },
  emptySelectContactHint: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  headerContainer: {
    padding: spacing.sm,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.secondary,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    height: 36,
    flex: 1,
    minWidth: 120,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    ...Platform.select({
      web: {
        outlineStyle: 'none' as any,
      },
    }),
  },
  bulkWhatsAppFAB: {
    position: 'absolute',
    right: spacing.lg,
    bottom: spacing.xl,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: 28,
    backgroundColor: '#25D366',
    gap: spacing.sm,
    zIndex: 999,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
      },
      android: {
        elevation: 6,
      },
      web: {
        position: 'fixed' as any,
        bottom: spacing.xl,
        right: spacing.xl,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      },
    }),
  },
  bulkWhatsAppFABText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.white,
  },
});
